/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import {
  IconDownload,
  IconPlus,
  IconMinus,
  IconSearch,
  IconX,
  IconTruck,
  IconArrowBack,
  IconPackage,
  IconRoute,
  IconLoader2,
  IconRotateClockwise,
  IconChevronDown,
  IconChevronUp,
} from "@tabler/icons-react";
import PremiumSearch from "../../../components/common/PremiumSearch";
import * as XLSX from "xlsx";
import ListView from "../../../components/common/ListView";
import TableSkeleton from "../../../components/skeleton/TableSkeleton";
import NoDataFound from "../../../components/common/NoDataFound";
import ManifestPagination from "./ManifestPagination";
import EntriesSelector from "../../../components/common/EntriesSelector";
import useDebounce from "../../../utilities/useDebounce";
import { useSelector } from "react-redux";
import { useMemo } from "react";
import toast from "react-hot-toast";
import formatDate, { formatDateTime } from "../../../utilities/helper";
import { useQuery, useQueryClient } from "@tanstack/react-query";

// Components
import NewSelectInput from "../../../components/NewSelectInput";
import NewinputField from "../../../components/NewInput";
import ColumnsFilter from "../../../components/common/ColumnsFilter";
import ButtonStyled from "../../../components/common/ButtonStyled";

// API Hooks
import { useFetchUnifiedManifestData } from "../../../apis/unifiedManifestApis";
import { usePickupRequestCreateMutation, useFetchAllPickupLocationData } from "../../../apis/pickupApis";
import RaidoField from "../../../components/common/RaidoField";
import { productManifestApi } from "../../../apis/productManifestApi";
import { useReturnToShipperMutation } from "../../../apis/returnToShipperApi";
import { useManifestUpdateMutation } from "../../../hooks/productManifest";
import { usePermission } from "../../../hooks/usePermissionTabs";

// Modals / Sidebar components
import GeneratePickupModal from "./GeneratePickupModal";
import ReturnToShipperModal from "./ReturnToShipperModal";
import ManifestDetailsSidebar from "./ManifestDetailsSidebar";
import ViewProductRequestModal from "../../PickupManagement/_components/ViewProductRequestModal"; 
import ToggleButton from "../../../components/ToggleButton";

type ManifestType = "pickup" | "linehaul" | "return" | "product" | "";

 

const TYPE_COLORS: Record<string, string> = {
  pickup: "bg-blue-50 text-blue-700 border-blue-200",
  linehaul: "bg-purple-50 text-purple-700 border-purple-200",
  return: "bg-orange-50 text-orange-700 border-orange-200",
  product: "bg-green-50 text-green-700 border-green-200",
};

const STATUS_COLORS: Record<string, string> = {
  GENERATED: "bg-slate-50 border border-slate-200 text-slate-700",
  ASSIGNED: "bg-slate-50 border border-slate-200 text-slate-700",
  ASSIGNED_TO_DRIVER: "bg-slate-50 border border-slate-200 text-slate-700",
  ASSIGNED_TO_STAFF: "bg-slate-50 border border-slate-200 text-slate-700",
  COLLECTED: "bg-slate-50 border border-slate-200 text-slate-700",
  COMPLETED: "bg-slate-50 border border-slate-200 text-slate-700",
  PENDING: "bg-slate-50 border border-slate-200 text-slate-700",
  RECEIVE_INBOUND: "bg-slate-50 border border-slate-200 text-slate-700",
  MANIFEST_REQUEST: "bg-slate-50 border border-slate-200 text-slate-700",
  VERIFIED: "bg-slate-50 border border-slate-200 text-slate-700",
  IN_PROCESS: "bg-slate-50 border border-slate-200 text-slate-700",
  ON_PROCESS: "bg-slate-50 border border-slate-200 text-slate-700",
  UPDATED: "bg-slate-50 border border-slate-200 text-slate-700",
  ASSIGN_TO_INVENTORY_STAFF:
    "bg-slate-50 border border-slate-200 text-slate-700",
  ADD_INVENTORY: "bg-slate-50 border border-slate-200 text-slate-700",
  ON_HOLD: "bg-slate-50 border border-slate-200 text-slate-700",
  CLOSED: "bg-slate-50 border border-slate-200 text-slate-700",
  CHECKED: "bg-slate-50 border border-slate-200 text-slate-700",
};

const vehicleTypes = [
  { id: 1, value: "car", text: "Car" },
  { id: 2, value: "van", text: "Van" },
  { id: 3, value: "dokker", text: "Dokker" },
];

const fmtStatus = (s: string) => {
  if (s === "ON_HOLD") return "Verified";
  if (s === "ON_PROCESS") return "In Process";
  return s
    ? s
      .toLowerCase()
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase())
    : "";
};

const t = (str: string) => str;

const SectionHeader = ({
  title,
}: {
  icon?: any;
  title: string;
}) => (
  <div className="mb-2">
    <p className="text-[#212121]/80 uppercase text-xs font-bold tracking-wider">
      {title}
    </p>
  </div>
);

interface Props { setHeaderButtons?: (n: React.ReactNode) => void; }

interface ProductItem {
  productId: string;
  quantity: number;
}

interface ProductFormValues {
  requestType: string;
  pickupDate: string;
  timeSlot: string;
  products: ProductItem[];
  reqType: string;
  packageType: string;
  vehicleType: string;
  locationId?: string;
}

export default function UnifiedManifestTab({ setHeaderButtons }: Props = {}) {
  const profile = useSelector((state: any) => state.auth.profile);
  const hasFMAccess = useMemo(() => {
    const access = profile?.access?.toUpperCase();
    return access === "FM" || access === "BOTH";
  }, [profile?.access]);

  const { allowed: canCreatePickup } = usePermission("pickup_manifests", "create");
  const { allowed: canCreateProduct } = usePermission("product_manifest", "create");
  const { allowed: canCreateReturn } = usePermission("return_to_shipper", "create");
  const canCreateLinehaul = false;
  const canCreateAnyManifest = canCreatePickup || canCreateProduct || canCreateReturn || canCreateLinehaul;

  const { allowed: canReadPickup } = usePermission("pickup_manifests", "read");
  const { allowed: canReadLinehaul } = usePermission("linehaul_manifests", "read");
  const { allowed: canReadReturn } = usePermission("return_to_shipper", "read");
  const { allowed: canReadProduct } = usePermission("product_manifest", "read");

  const filterOptions = [
    ...(canReadPickup ? [{ id: "pickup", value: "pickup", text: "Shipment" }] : []),
    ...(canReadLinehaul ? [{ id: "linehaul", value: "linehaul", text: "Linehaul" }] : []),
    ...(canReadReturn ? [{ id: "return", value: "return", text: "Return to Shipper" }] : []),
    ...(canReadProduct && hasFMAccess ? [{ id: "product", value: "product", text: "Product" }] : []),
  ];

  const today = new Date().toISOString().split("T")[0];
  const { data: allPickupLocationData } = useFetchAllPickupLocationData();
  const [pageNo, setPageNo] = useState(1);
  const [limit, setLimit] = useState(20);
  const [searchFor, setSearchFor] = useState("");
  const expandedIdsRef = useRef<Set<string>>(new Set());
  const [, forceUpdate] = useState(0);

  const toggleRow = (id: string) => {
    if (expandedIdsRef.current.has(id)) {
      expandedIdsRef.current.delete(id);
    } else {
      expandedIdsRef.current.add(id);
    }
    forceUpdate((n) => n + 1);
  };
  const [typeFilter, setTypeFilter] = useState<ManifestType>("");
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [showSearch, setShowSearch] = useState(false);
  const debouncedSearch = useDebounce(searchFor, 500);

  // Sidebar / Details States
  const [selectedManifest, setSelectedManifest] = useState<any>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createType, setCreateType] = useState<ManifestType>("");
  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [initialUpdateMode, setInitialUpdateMode] = useState(false);
  const [pickupStep, setPickupStep] = useState<number>(1);

  const [columnsData, setColumnsData] = useState([
    { text: "SN", value: "sn", isShow: true },
    { text: "Manifest ID", value: "manifestId", isShow: true },
    { text: "Merchant", value: "client", isShow: true },
    { text: "Type", value: "type", isShow: true },
    { text: "CreatedBy", value: "createdBy", isShow: true },
    { text: "Date", value: "date", isShow: true },
    { text: "Status", value: "status", isShow: true },
  ]);

  const setHeadings = (value: string | undefined, checked: boolean) => {
    setColumnsData((prev) =>
      prev.map((col) =>
        col.value === value ? { ...col, isShow: checked } : col
      )
    );
  };

  // Mutations
  const { mutate: createPickupRequest, isPending: createPickupPending } = usePickupRequestCreateMutation();
  const { mutate: createReturnRequest, isPending: createReturnPending } = useReturnToShipperMutation();

  const queryClient = useQueryClient();
  const { mutate: updateManifest, isPending: isProductUpdating } = useManifestUpdateMutation(selectedManifest?.id || selectedManifest?.manifestId || "");

  const handleUpdateManifest = (formData: any) => {
    const { locationId, vehicleType, ...rest } = formData;

    let payload =
      formData.requestType === "dropRequest" || formData.requestType === "DROP"
        ? rest
        : formData;

    // Filter products when split is enabled - only include selected products
    if (formData.split === "true" && formData.products) {
      const selectedProducts = formData.products.filter((product: any) => product.selected);
      payload.products = selectedProducts;
    }

    payload.status =
      selectedManifest?.status === "MANIFEST_REQUEST"
        ? "UPDATED"
        : selectedManifest?.status === "ON_HOLD"
          ? "VERIFIED"
          : "";

    // Include packageType and reqType in payload
    if (formData.packageType) {
      payload.packageType = formData.packageType;
      payload.reqType = (formData.packageType === "Pallet" || formData.packageType === "PALLET") ? "PALLET" : "QUANTITY";
    }

    // Explicitly include split field in payload
    payload.split = formData.split || false;

    const normalizedPayload = {
      ...payload,
      products: (payload.products || []).map((p: any) => ({
        ...p,
        quantity: Number(p.quantity),
      })),
    };

    updateManifest(normalizedPayload as any, {
      onSuccess: () => {
        productReset();
        setIsProductModalOpen(false);
        refetch();
        queryClient.invalidateQueries({ queryKey: ["productDetails", selectedManifest?.id || selectedManifest?.manifestId] });
        queryClient.invalidateQueries({ queryKey: ["manifest", selectedManifest?.id || selectedManifest?.manifestId] });
        toast.success("Product Manifest updated successfully!");
      },
      onError: (error: any) => {
        console.error("Error in updating manifest:", error);
        toast.error("Failed to update product manifest");
      },
    });
  };

  // React Hook Forms
  const {
    register: pickupRegister, setValue: pickupSetValue, watch: pickupWatch,
    control: pickupControl, clearErrors: pickupClearErrors, handleSubmit: handlePickupSubmit,
    reset: pickupReset, formState: { errors: pickupErrors },
  } = useForm();

  const {
    register: productRegister, setValue: productSetValue, watch: productWatch,
    control: productControl, clearErrors: productClearErrors, handleSubmit: handleProductSubmit,
    reset: productReset, formState: { errors: productErrors },
  } = useForm<ProductFormValues>({
    defaultValues: {
      requestType: "pickupRequest",
      pickupDate: "",
      timeSlot: "",
      products: [],
      reqType: "QUANTITY",
      packageType: "",
      vehicleType: "",
      locationId: "",
    }
  });

  const {
    register: returnRegister, setValue: returnSetValue, watch: returnWatch,
    control: returnControl, clearErrors: returnClearErrors, handleSubmit: handleReturnSubmit,
    reset: returnReset, formState: { errors: returnErrors },
  } = useForm({
    defaultValues: {
      requestType: "PICKUP",
      awbs: [],
    }
  });

  const { refetch, isLoading, isFetching } = useFetchUnifiedManifestData(
    pageNo, limit, debouncedSearch, typeFilter || undefined
  );

  const data = useSelector((state: any) => state.unifiedManifest.list);
  const manifests: any[] = data?.manifests ?? [];

  const maxStatusWidth = React.useMemo(() => {
    if (!manifests.length) return "140px";
    const maxLen = manifests.reduce((max, m) => {
      const text = fmtStatus(m.status);
      return text.length > max ? text.length : max;
    }, 0);
    const calculatedWidth = Math.max(140, maxLen * 7.2 + 28);
    return `${calculatedWidth}px`;
  }, [manifests]);

  useEffect(() => { refetch(); }, [pageNo, limit, debouncedSearch, typeFilter]);

  // Set top header button hook
  useEffect(() => {
    if (!setHeaderButtons) return;
    setHeaderButtons(
      <div className="flex items-center gap-2 bg-white/70 backdrop-blur-sm px-4 py-1.5 rounded-lg border border-slate-200 shadow-sm text-[11px] select-none font-bold min-h-8">
        <div className="flex items-center gap-1.5">
          <span className="text-slate-500">Total:</span>
          <span className="text-blue-700 bg-blue-50 border border-blue-100 px-1.5 py-0.5 rounded">
            {data?.stats?.total ?? data?.total ?? 0}
          </span>
        </div>
        <span className="h-3 w-px bg-slate-200" />
        <div className="flex items-center gap-1.5">
          <span className="text-slate-500">Draft:</span>
          <span className="text-rose-700 bg-rose-50 border border-rose-100 px-1.5 py-0.5 rounded">
            {data?.stats?.draft ?? 0}
          </span>
        </div>
        <span className="h-3 w-px bg-slate-200" />
        <div className="flex items-center gap-1.5">
          <span className="text-slate-500">Active:</span>
          <span className="text-indigo-700 bg-indigo-50 border border-indigo-100 px-1.5 py-0.5 rounded">
            {data?.stats?.active ?? 0}
          </span>
        </div>
        <span className="h-3 w-px bg-slate-200" />
        <div className="flex items-center gap-1.5">
          <span className="text-slate-500">Verified:</span>
          <span className="text-green-700 bg-green-50 border border-green-100 px-1.5 py-0.5 rounded">
            {data?.stats?.verified ?? 0}
          </span>
        </div>
      </div>
    );
    return () => setHeaderButtons?.(null);
  }, [setHeaderButtons, data?.stats, data?.total]);

  // Export to Excel
  const handleExport = () => {
    if (manifests.length === 0) {
      toast.error("No manifests to export");
      return;
    }
    const excelData = manifests.map((m) => ({
      "Manifest ID": m.manifestId || m.id,
      "Type": m.type,
      "Client": m.clientName || m.clientId || "Self",
      "Status": m.status,
      "AWB/Item": m.qty || 0,
      "Date": formatDate(m.createdAt || m.date),
    }));
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Unified Manifests");
    XLSX.writeFile(workbook, "unified_manifests.xlsx");
    toast.success("Manifests exported successfully ✅");
  };

  // Submit pickup request manifest
  const onPickupSubmit = (formData: any) => {
    if (!selectedOrders.length) {
      toast.error("Please select at least one shipment");
      return;
    }

    let pickupDate = "";
    let timeSlot = "";
    if (formData.pickupDateTime) {
      const [datePart, timePart] = formData.pickupDateTime.split("T");
      pickupDate = datePart;
      timeSlot = timePart ? `${timePart}:00` : "00:00:00";
    }

    const payload = {
      requestType: formData.requestType,
      vehicleType: formData.vehicleType,
      locationId: formData.locationId,
      pickupDate,
      timeSlot,
      specialInstructions: formData.description || "",
      shipmentsId: selectedOrders,
    };
    createPickupRequest(payload, {
      onSuccess: () => {
        setIsCreateOpen(false);
        pickupReset();
        setSelectedOrders([]);
        setPickupStep(1);
        refetch();
        toast.success("Pickup Manifest request submitted successfully!");
      },
      onError: (err: any) => {
        toast.error(err?.message || "Failed to create pickup request");
      }
    });
  };

  // Submit product request manifest
  const [isProductSubmitting, setIsProductSubmitting] = useState(false);
  const onProductSubmit = async (formData: any) => {
    const formattedProducts = formData.products?.filter((p: any) => p.productId && p.quantity > 0);
    if (!formattedProducts?.length) {
      toast.error("Please add at least one product with quantity");
      return;
    }
    const reqType = (formData.packageType === "Pallet" || formData.packageType === "PALLET") ? "PALLET" : "QUANTITY";
    const { locationId, vehicleType, ...rest } = formData;
    const basePayload = (formData.requestType === "dropRequest" || formData.requestType === "DROP") ? rest : formData;
    const payload = {
      ...basePayload,
      reqType,
      products: formattedProducts,
    };
    try {
      setIsProductSubmitting(true);
      const res = await productManifestApi.createManifest(payload);
      if (res?.success) {
        setIsCreateOpen(false);
        productReset();
        setProductRows([]);
        refetch();
        toast.success("Product Manifest request submitted successfully!");
      } else {
        toast.error(res?.message || "Failed to create product manifest");
      }
    } catch (err: any) {
      toast.error(err?.message || "An error occurred");
    } finally {
      setIsProductSubmitting(false);
    }
  };

  // Submit return request manifest
  const onReturnSubmit = (formData: any) => {
    if (!formData.awbs || !formData.awbs.length) {
      toast.error("Please enter at least one AWB number");
      return;
    }
    const payload = {
      requestType: formData.requestType,
      awbs: formData.awbs,
    };
    createReturnRequest(payload, {
      onSuccess: () => {
        setIsCreateOpen(false);
        returnReset();
        refetch();
        toast.success("Return Manifest request submitted successfully!");
      },
      onError: (err: any) => {
        toast.error(err?.message || "Failed to create return request");
      }
    });
  };

  const showRightPanel = isDetailsOpen || isCreateOpen;

  // React Hook Form FieldArray support for product rows
  const [productRows, setProductRows] = useState<{ productId: string; quantity: number }[]>([]);

  useEffect(() => {
    if (createType === "product" && productRows.length === 0) {
      const initialRow = { productId: "", quantity: 0 };
      setProductRows([initialRow]);
      productSetValue("products", [initialRow]);
    }
  }, [createType]);

  const { data: productsListResponse } = useQuery({
    queryKey: ["allProducts"],
    queryFn: async () => {
      const res = await productManifestApi.getAllProducts();
      return res?.data;
    }
  });

  const productsList = productsListResponse || [];

  const columns = columnsData.filter((c) => c.isShow).map((col) => {
    switch (col.value) {
      case "sn":
        return {
          label: "#",
          render: (_: any, i: number) => (pageNo - 1) * limit + (i + 1),
        };

      case "manifestId":
        return {
          label: t("Manifest ID"),
          render: (row: any) => {
            const hasChildren =
              row.type === "product" &&
              row.children &&
              row.children.length > 0;
            const isExpanded = expandedIdsRef.current.has(row.id);
            return (
              <div className="flex items-center justify-start space-x-2">
                {hasChildren && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleRow(row.id);
                    }}
                    className="flex h-7 w-7 items-center justify-center rounded-lg border border-[#ECECEC] bg-[#F7F6F2] hover:bg-gray-100 transition"
                    title={isExpanded ? t("Collapse") : t("Expand")}
                  >
                    {isExpanded ? (
                      <IconChevronUp size={16} />
                    ) : (
                      <IconChevronDown size={16} />
                    )}
                  </button>
                )}
                <span className="text-xs text-[#212121]">{row.manifestId}</span>
              </div>
            );
          },
        };
      case "type":
        return {
          label: t("Type"),
          render: (row: any) => (
            <div className="text-left text-xs text-[#212121]">
              {t(row.typeLabel)}
            </div>
          ),
        };
      case "client":
        return {
          label: t("Merchant"),
          render: (row: any) => (
            <div className="text-left text-xs text-[#212121]">{t(row.client)}</div>
          ),
        };
      case "qty":
        return {
          label: t("AWB/Item"),
          render: (row: any) => (
            <div className="text-left text-xs text-[#212121]">{row.qty}</div>
          ),
        };
      case "date":
        return {
          label: t("Date"),
          render: (row: any) => (
            <div className="text-left text-xs text-[#212121]">
              {formatDateTime(row.date)}
            </div>
          ),
        };
      case "createdBy":
        return {
          label: t("Created By"),
          render: (row: any) => {
            return (
              <div className="text-left text-xs text-[#212121]">
                {row.createdByName || t(row.createdByType) || "—"}
              </div>
            );
          },
        };

      case "status":
        return {
          label: t("Status"),
          render: (row: any) => (
            <div className="flex items-center justify-start text-left">
              <span
                className={`inline-flex items-center justify-start rounded-lg py-1 px-4 text-xs font-semibold whitespace-nowrap text-left border shadow-sm select-none transition-all duration-150 hover:shadow ${STATUS_COLORS[row.status] || "bg-slate-50 border-slate-200 text-slate-700"}`}
              >
                {t(fmtStatus(row.status))}
              </span>
            </div>
          ),
        };

      default:
        return { label: "", render: () => null };
    }
  });

  const renderExpandedContent = (row: any) => {
    if (row.type !== "product") return null;
    if (!expandedIdsRef.current.has(row.id)) return null;
    if (!row.children?.length) return null;

    return (
      <div className="bg-slate-50 border-t border-slate-200 px-6 py-4">
        <div className="rounded-xl overflow-hidden border border-slate-200 bg-white">
          <div className="grid grid-cols-4 bg-slate-100 text-xs font-semibold text-slate-600 px-4 py-3">
            <div>{t("Manifest")}</div>
            <div>{t("Created By")}</div>
            <div>{t("Date")}</div>
            <div>{t("Status")}</div>
          </div>

          {row.children.map((child: any) => (
            <div
              key={child.id}
              onDoubleClick={(e) => {
                e.stopPropagation();
                setSelectedManifest({
                  ...child,
                  type: "product",
                });
                setIsCreateOpen(false);
                setIsDetailsOpen(true);
              }}
              className="grid grid-cols-4 items-center px-4 py-3 border-t hover:bg-yellow-50 cursor-pointer transition select-none"
            >
              <div>
                <p className="text-sm text-slate-600">
                  {child.manifestId}
                </p>
              </div>

              <div className="text-sm text-slate-600">
                {child.createdBy}
              </div>
              <div className="text-sm text-slate-600">
                {formatDateTime(child.createdAt || child.date)}
              </div>

              <div>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${STATUS_COLORS[child.status] || "bg-slate-50 border-slate-200 text-slate-700"
                    }`}
                >
                  {fmtStatus(child.status)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="grid grid-cols-12 gap-6 p-3 bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl transition-all duration-300 rounded-lg w-full">
        <div className="absolute inset-0 rounded-lg bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
        <div className={showRightPanel ? "col-span-12 xl:col-span-7 z-10" : "col-span-12 z-10"}>
          {/* Header + Table wrapped in bordered card — same as admin */}
          <div className="border rounded-lg overflow-visible">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 px-1 py-3.5">
              {/* Left side (empty — tabs handled by parent) */}
              <div className="flex flex-wrap items-center gap-3"></div>

              {/* Right side: Action controls */}
              <div className="flex flex-wrap items-center gap-2.5 sm:justify-end">
                <NewSelectInput
                  name="type-filter"
                  placeholder="Type"
                  value={typeFilter}
                  setValue={(val) => {
                    setTypeFilter(val || "");
                    setPageNo(1);
                  }}
                  selectOptions={filterOptions}
                  className="w-fit"
                  removeDiv
                  compact
                />
                {(typeFilter || searchFor) && (
                  <button
                    onClick={() => {
                      setTypeFilter("");
                      setSearchFor("");
                      setShowSearch(false);
                      setPageNo(1);
                    }}
                    className="w-8 h-8 flex items-center justify-center border border-slate-200 text-[#212121] rounded-lg transition-all duration-150 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 cursor-pointer disabled:opacity-50"
                    title={t("Reset Filters")}
                  >
                    <IconRotateClockwise size={14} stroke={2.5} />
                  </button>
                )}

                {/* Premium Search */}
                <PremiumSearch
                  value={searchFor}
                  onChange={(e) => {
                    setSearchFor(e.target.value);
                    setPageNo(1);
                  }}
                />

                {/* Export */}
                <div className="relative group">
                  <button
                    onClick={handleExport}
                    className="w-8 h-8 flex items-center justify-center bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-lg transition-all duration-150 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 cursor-pointer"
                  >
                    <IconDownload size={17} stroke={2.5} />
                  </button>
                  <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-800 text-white text-[10px] font-bold px-2 py-1 rounded shadow-md whitespace-nowrap pointer-events-none z-50">
                    {t("Export")}
                  </div>
                </div>

                {/* Entries Selector */}
                {data && data.total > 10 && (
                  <EntriesSelector
                    value={limit}
                    options={[
                      10,
                      ...(data.total > 10 ? [20] : []),
                      ...(data.total > 20 ? [30] : []),
                      ...(data.total > 50 ? [50] : []),
                      ...(data.total > 100 ? [100] : []),
                    ]}
                    onChange={(val) => {
                      setLimit(val);
                      setPageNo(1);
                      setSelectedRows([]);
                    }}
                  />
                )}

                {/* Columns Filter */}
                <div className="relative group">
                  <ColumnsFilter
                    columnsData={columnsData.filter(
                      (c) => c.value !== "sn",
                    )}
                    setHeadings={(val, checked) =>
                      setColumnsData((prev) =>
                        prev.map((c) =>
                          c.value === val ? { ...c, isShow: checked } : c,
                        ),
                      )
                    }
                  />
                  <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-800 text-white text-[10px] font-bold px-2 py-1 rounded shadow-md whitespace-nowrap pointer-events-none z-50">
                    {t("Columns View")}
                  </div>
                </div>

                {/* Create Manifest button */}
                {(canCreatePickup || canCreateProduct || canCreateReturn) && (
                  <ButtonStyled
                    onClick={() => {
                      setIsDetailsOpen(false);
                      setIsCreateOpen(true);
                    }}
                    variant={isCreateOpen ? "solid" : "outline"}
                    icon={<IconPlus className="w-3.5 h-3.5" />}
                    value={t("Create Manifest")}
                  />
                )}
              </div>
            </div>

            {/* Table */}
            <div>
              {isLoading ? (
                <div>
                  <TableSkeleton
                    columns={columnsData
                      .filter((c) => c.isShow)
                      .map((c) => c.text)}
                    rows={6}
                  />
                </div>
              ) : manifests.length > 0 ? (
                <ListView
                  onRowDoubleClick={(row: any) => {
                    setSelectedManifest(row);
                    setIsCreateOpen(false);
                    setIsDetailsOpen(true);
                  }}
                  data={manifests}
                  columns={columns}
                  thClassName="text-left"
                  tdClassName="text-left"
                  // renderExpandedContent={renderExpandedContent}
                  // expandedRows={{ has: () => true } as any}
                />
              ) : (
                <NoDataFound
                  title="No Data Found"
                  message="Looks like you haven't added anything yet."
                />
              )}
            </div>
          </div>
        </div>

        {/* Sidebar panels */}
        {showRightPanel && (
          <>
            {/* Backdrop overlay for mobile/tablet */}
            {/* <div
              className="lg:hidden fixed inset-0 bg-black/40 z-40"
              onClick={() => {
                setIsCreateOpen(false);
                setIsDetailsOpen(false);
                setCreateType("");
                pickupReset();
                productReset();
                setProductRows([]);
                returnReset();
                setSelectedOrders([]);
                setPickupStep(1);
              }}
            /> */}
            <div className="col-span-12 xl:col-span-5">
              {isDetailsOpen && selectedManifest && !isProductModalOpen && (
                <ManifestDetailsSidebar
                  manifest={selectedManifest}
                  onClose={() => setIsDetailsOpen(false)}
                  onUpdateProductManifest={() => {
                    setInitialUpdateMode(true);
                    setIsProductModalOpen(true);
                  }}
                  onVerifyIfHold={() => {
                    setInitialUpdateMode(false);
                    setIsProductModalOpen(true);
                  }}
                />
              )}

              {isProductModalOpen && (
                <ViewProductRequestModal
                  open={isProductModalOpen}
                  onClose={() => {
                    setIsProductModalOpen(false);
                  }}
                  manifestId={selectedManifest?.id || selectedManifest?.manifestId}
                  register={productRegister as any}
                  setValue={productSetValue as any}
                  watch={productWatch as any}
                  errors={productErrors as any}
                  control={productControl as any}
                  handleSubmit={handleProductSubmit(handleUpdateManifest) as any}
                  onSplitSuccess={() => {
                    refetch();
                  }}
                  reset={productReset as any}
                  initialUpdateMode={initialUpdateMode}
                />
              )}
              {isCreateOpen && (
                <div className="relative border rounded-lg border-gray-200 flex flex-col overflow-hidden w-full h-auto max-h-[calc(100vh-115px)] lg:sticky lg:top-[90px]">
                  {/* Header */}
                  <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-transparent flex-shrink-0">
                    <h2 className="text-xl font-semibold text-[#212121]">Create Manifest</h2>
                    <div className="flex items-center gap-2">
                      {createType === "pickup" && pickupStep === 2 && (
                        <ButtonStyled
                          type="button"
                          variant="outline"
                          onClick={() => setPickupStep(1)}
                          value="Back"
                        />
                      )}
                      <NewSelectInput
                        name="createType"
                        value={createType}
                        onChange={(val) => {
                          setCreateType(val as ManifestType);
                          setPickupStep(1);
                        }}
                        selectOptions={[
                          ...(canCreatePickup ? [{ id: "pickup", value: "pickup", text: "Shipment" }] : []),
                          ...(canCreateProduct && hasFMAccess ? [{ id: "product", value: "product", text: "Product" }] : []),
                          ...(canCreateReturn ? [{ id: "return", value: "return", text: "Return" }] : []),
                        ]}
                        placeholder="Select"
                        removeDiv
                        compact
                        className="w-28 text-xs select-none"
                      />
                      <ButtonStyled
                        type="button"
                        onClick={() => {
                          if (createType === "pickup") {
                            if (pickupStep === 1) {
                              if (!selectedOrders.length) {
                                toast.error("Please select at least one shipment to continue.");
                                return;
                              }
                              setPickupStep(2);
                            } else {
                              handlePickupSubmit(onPickupSubmit)();
                            }
                          }
                          if (createType === "product") handleProductSubmit(onProductSubmit)();
                          if (createType === "return") handleReturnSubmit(onReturnSubmit)();
                        }}
                        disabled={!createType || createPickupPending || isProductSubmitting || createReturnPending}
                        isPending={createPickupPending || isProductSubmitting || createReturnPending}
                        variant="solid"
                        value={createType === "pickup" && pickupStep === 1 ? "Continue" : "Create"}
                      />
                      <button
                        onClick={() => {
                          setIsCreateOpen(false);
                          setCreateType("");
                          pickupReset();
                          productReset();
                          setProductRows([]);
                          returnReset();
                          setSelectedOrders([]);
                          setPickupStep(1);
                        }}
                        className="p-1.5 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors cursor-pointer"
                      >
                        <IconX size={18} />
                      </button>
                    </div>
                  </div>

                  {/* Form fields */}
                  <div className="flex-1 overflow-y-auto pb-6">
                    {!createType ? (
                      <div className="flex flex-col items-center justify-center p-8 text-center h-[50vh] select-none">
                        <div className="p-4 rounded-full bg-slate-50 text-slate-300 mb-3 border border-slate-100/80 shadow-sm">
                          <IconPackage size={48} stroke={1.5} />
                        </div>
                        <h3 className="text-sm font-bold text-[#212121]">Select Manifest Type</h3>
                        <p className="text-xs text-slate-400 mt-1.5 max-w-[240px] leading-relaxed">
                          Choose a manifest type from the dropdown above to configure your new manifest details.
                        </p>
                      </div>
                    ) : (
                      <>
                        {createType === "pickup" && (
                          <GeneratePickupModal
                            register={pickupRegister}
                            errors={pickupErrors}
                            clearErrors={pickupClearErrors}
                            setValue={pickupSetValue}
                            control={pickupControl}
                            selectedOrders={selectedOrders}
                            setSelectedOrders={setSelectedOrders}
                            watch={pickupWatch}
                            pickupStep={pickupStep}
                          />
                        )}

                        {createType === "product" && (
                          <div className="w-full py-4 px-6 bg-transparent space-y-6">
                            <div className="space-y-4">
                              <div className="flex items-center justify-between flex-wrap gap-3">
                                <p className="text-[#212121]/80 uppercase text-xs font-bold tracking-wider">
                                  Product Request Setup
                                </p>

                                <RaidoField
                                  label=""
                                  value={productWatch("requestType") || "pickupRequest"}
                                  onClick={(val) => {
                                    if (typeof val === "string") {
                                      productSetValue("requestType", val);
                                    }
                                  }}
                                  valuesArr={[
                                    { value: "pickupRequest", text: "Pickup Request" },
                                    { value: "dropRequest", text: "Drop off Request" },
                                  ]}
                                />
                              </div>

                                <div className="grid grid-cols-2 gap-3">
                                  <NewSelectInput
                                    label=" Package Type"
                                    name="packageType"
                                    value={productWatch("packageType")}
                                    clearErrors={productClearErrors}
                                    register={productRegister}
                                    error={productErrors?.packageType}
                                    validation={{ required: "Package Type is required" }}
                                    onChange={(val) => {
                                      productSetValue("packageType", val);
                                      if (val === "PALLET" || val === "Pallet") {
                                        productSetValue("reqType", "PALLET");
                                      } else {
                                        productSetValue("reqType", "QUANTITY");
                                      }
                                    }}
                                    selectOptions={[
                                      { id: "PALLET", value: "PALLET", text: "Pallet" },
                                      { id: "BOX", value: "BOX", text: "Box" },
                                    ]}
                                    className="flex flex-col gap-1"
                                    fullWidthDropdown={true}
                                  />

                                  {(productWatch("requestType") || "pickupRequest") === "pickupRequest" && (
                                    <NewSelectInput
                                      label="Vehicle Type"
                                      name="vehicleType"
                                      value={productWatch("vehicleType")}
                                      clearErrors={productClearErrors}
                                      register={productRegister}
                                      error={productErrors?.vehicleType}
                                      validation={{ required: "Vehicle Type is required" }}
                                      onChange={(val) => productSetValue("vehicleType", val)}
                                      selectOptions={vehicleTypes}
                                      className="flex flex-col gap-1"
                                      fullWidthDropdown={true}
                                    />
                                  )}

                                  {(productWatch("requestType") || "pickupRequest") === "pickupRequest" && (
                                    <NewSelectInput
                                      label="Location"
                                      name="locationId"
                                      value={productWatch("locationId")}
                                      clearErrors={productClearErrors}
                                      register={productRegister}
                                      error={productErrors?.locationId}
                                      validation={{ required: "Location is required" }}
                                      onChange={(val) => productSetValue("locationId", val)}
                                      selectOptions={
                                        allPickupLocationData?.map((loc: any) => ({
                                          id: loc.id,
                                          value: loc.id,
                                          text: loc.full_address,
                                        })) || []
                                      }
                                      className="flex flex-col gap-1"
                                      fullWidthDropdown={true}
                                    />
                                  )}

                                  <NewinputField
                                    title="Pickup Date"
                                    type="date"
                                    name="pickupDate"
                                    register={productRegister}
                                    error={productErrors?.pickupDate}
                                    validation={{ required: "Pickup Date is required" }}
                                    min={today}
                                    className="flex flex-col gap-1"
                                  />

                                  <NewinputField
                                    title="Preferred Time Slot"
                                    type="time"
                                    name="timeSlot"
                                    register={productRegister}
                                    error={productErrors?.timeSlot}
                                    validation={{ required: "Time Slot is required" }}
                                    className="flex flex-col gap-1"
                                  />
                                </div>
                              </div>

                            {/* Products select array */}
                            <div className="space-y-3">
                              <div className="flex items-center justify-between">
                                <SectionHeader icon={IconPackage} title="Select Products" />
                                <div className="flex items-center gap-3">
                                  <div className="flex items-center gap-1.5">
                                    <ToggleButton
                                      onChange={() =>
                                        productSetValue(
                                          "reqType",
                                          productWatch("reqType") === "QUANTITY"
                                            ? "PALLET"
                                            : "QUANTITY"
                                        )
                                      }
                                      checked={productWatch("reqType") === "PALLET" ? true : false}
                                    />
                                    <span className="text-[#3A3A3C] text-xs font-semibold">
                                      Send By Pallet
                                    </span>
                                  </div>

                                  <button
                                    type="button"
                                    onClick={() => {
                                      const next = [...productRows, { productId: "", quantity: 0 }];
                                      setProductRows(next);
                                      productSetValue("products", next);
                                    }}
                                    className="p-1.5 bg-[#FDF3C6] hover:bg-[#FBD037] text-black border border-[#F5E296] rounded-lg transition-colors cursor-pointer flex items-center justify-center"
                                    title="Add Product"
                                  >
                                    <IconPlus size={16} />
                                  </button>
                                </div>
                              </div>

                              <div className="flex flex-col gap-3">
                                {productRows.map((row, index) => (
                                  <div key={index} className="flex items-end gap-2 w-full">
                                    <div className="grid grid-cols-2 gap-3 flex-1">
                                      <NewSelectInput
                                        label="Product"
                                        name={`products.${index}.productId`}
                                        value={row.productId}
                                        onChange={(val) => {
                                          const next = [...productRows];
                                          next[index].productId = val;
                                          setProductRows(next);
                                          productSetValue("products", next);
                                        }}
                                        selectOptions={productsList.flatMap((p: any) => {
                                          if (p.variants && p.variants.length > 0) {
                                            // Product has variants — show each variant individually
                                            return p.variants.map((v: any) => ({
                                              id: v.id,
                                              value: v.id,
                                              text: `${v.sku || p.sku} (${p.itemName})`,
                                            }));
                                          }
                                          // No variants — show the parent product
                                          return [{
                                            id: p.id,
                                            value: p.id,
                                            text: `${p.sku} (${p.itemName})`,
                                          }];
                                        })}
                                        className="flex flex-col gap-1"
                                        fullWidthDropdown={true}
                                      />

                                      <NewinputField
                                        title="Qty"
                                        type="number"
                                        name={`products.${index}.quantity`}
                                        value={row.quantity?.toString()}
                                        onChange={(e: any) => {
                                          const next = [...productRows];
                                          next[index].quantity = parseInt(e.target.value) || 0;
                                          setProductRows(next);
                                          productSetValue("products", next);
                                        }}
                                        className="flex flex-col gap-1"
                                        inputClassName="w-full rounded-xl py-[10px] px-4 outline-none text-xs bg-yellow-50 border border-[#D8D8D8] text-[#1E1E1E] focus:border-yellow-500 transition h-[38px] leading-tight"
                                      />
                                    </div>

                                    <button
                                      type="button"
                                      onClick={() => {
                                        if (productRows.length > 1) {
                                          const next = productRows.filter((_, i) => i !== index);
                                          setProductRows(next);
                                          productSetValue("products", next);
                                        }
                                      }}
                                      className="p-2 bg-[#FDF3C6] hover:bg-red-100 hover:border-red-300 text-black rounded-lg border border-[#F5E296] transition-colors cursor-pointer h-[38px] flex items-center justify-center shrink-0 mb-[1px]"
                                      title="Remove Product"
                                    >
                                      <IconMinus size={16} />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}

                        {createType === "return" && (
                          <ReturnToShipperModal
                            register={returnRegister}
                            errors={returnErrors}
                            setValue={returnSetValue}
                            watch={returnWatch}
                            clearErrors={returnClearErrors}
                            control={returnControl}
                            reset={returnReset}
                          />
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* Pagination - spans full 12 columns at bottom of the grid */}
        {data?.total > 0 && (
          <div className="col-span-12 z-10 px-2">
            <ManifestPagination
              currentPage={pageNo}
              totalPages={Math.ceil((data?.total || 0) / limit)}
              fetchData={(page: number) => setPageNo(page)}
              totalCount={data?.total}
              itemLabel="manifests"
            />
          </div>
        )}
      </div>

    </>
  );
}
